import React, { useEffect, useState } from "react";
import { ref, push, set, get } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";
import { useParams, Link } from "react-router-dom";
import Popup from "../components/Popup";

const PlayerReg = () => {
  const { gameId } = useParams();
  const [auctionSettings, setAuctionSettings] = useState(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch auction settings
        const settingsRef = ref(db, `games/${gameId}/auctionSettings`);
        const settingsSnapshot = await get(settingsRef);

        if (settingsSnapshot.exists()) {
          const settings = settingsSnapshot.val();
          setAuctionSettings(settings);
        }
      } catch (error) {}
    };
    fetchData();
  }, [gameId]);
  const [player, setPlayer] = useState({
    name: "",
    role: "",
    price: auctionSettings?.defaultBasePrice || 0,
    status: "upcoming",
    image: "",
    playerNumber: "",
    mobileNo: "",
    fileId: null,
  });
  const { uploadImage, uploading, clearError, error: uploadError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (f, v) => {
    setPlayer({ ...player, [f]: v });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setPreview(null);
      setPlayer({ ...player, image: "", fileId: null });
      return;
    }

    // If there's an existing uploaded file, delete it first
    if (player.fileId) {
      await deleteFile(player.fileId);
    }

    // Create preview first
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);

    // Upload immediately when file is selected
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      // Store the uploaded file info in player state
      setPlayer({
        ...player,
        image: result.fileUrl,
        fileId: result.fileId,
      });
    } else {
      // Show error and clear preview
      setPopup({
        isOpen: true,
        message: result.error || "Failed to upload image",
        type: "error",
      });
      setPreview(null);
      e.target.value = ""; // Reset file input
    }
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();

    if (!gameId) {
      setPopup({ isOpen: true, message: "Game ID missing", type: "error" });
      return;
    }

    // Check if image is still uploading
    if (uploading) {
      setPopup({
        isOpen: true,
        message: "Please wait for image upload to complete",
        type: "info",
      });
      return;
    }

    setLoading(true);
    const requestRef = push(ref(db, `player-req/${gameId}/players`));

    await set(requestRef, {
      name: player.name,
      role: player.role,
      price: player.price,
      status: "upcoming",
      soldTo: null,
      image: player.image,
      playerNumber: player.playerNumber,
      mobileNo: player.mobileNo,
      fileId: player.fileId || null,
    });

    setPopup({
      isOpen: true,
      message: "Player registered successfully! You can now close this page.",
      type: "success",
    });
    setPlayer({
      name: "",
      role: "",
      image: "",
      playerNumber: "",
      mobileNo: "",
      price: auctionSettings?.defaultBasePrice || 0,
      fileId: null,
    });
    setPreview(null);
    setLoading(false);
  };

  return (
    <div className="min-vh-100">
      <div className="container-fluid pt-2 px-4">
        <div
          className="card shadow-lg border-0"
          style={{
            borderRadius: "20px",
            overflow: "hidden",
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          {/* Header */}
          <div
            className="card-header text-white text-center py-5 bg-info"
            // style={{ background: "#5a67d8" }}
          >
            {/* <div className="mb-3">
              <i className="bi bi-person-plus-fill" style={{ fontSize: "4rem" }}></i>
            </div> */}
            <h1 className="display-4 fw-bold mb-3">Player Registration</h1>
            <p className="lead mb-4">
              Register yourself for the upcoming auction event
            </p>
            <div className="d-inline-block px-4 py-2 bg-white bg-opacity-25 rounded-pill">
              <i className="bi bi-trophy-fill me-2"></i>
              SportsBid Arena
            </div>
          </div>

          {/* Registration Form */}
          <div className="card-body p-5">
            <div className="text-center mb-4">
              <h2 className="h4 fw-bold mb-2">Fill Your Details</h2>
              <p className="text-muted">
                All fields marked with * are required
              </p>
            </div>

            <form onSubmit={handleAddPlayer}>
              <div className="row g-4">
                {/* Player Photo Upload */}
                <div className="col-12 text-center">
                  <div className="player-photo-upload mb-3">
                    {preview ? (
                      <div className="photo-preview">
                        <img
                          src={preview}
                          alt="Player preview"
                          className="rounded-circle"
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger remove-photo"
                          onClick={async () => {
                            // Delete file from storage if it exists
                            if (player.fileId) {
                              try{
                                setDeleting(true);
                                await deleteFile(player.fileId);
                              }
                              catch( e){

                              }
                              finally{
                                setDeleting(false)
                              }

                            }
                            setPreview(null);
                            setPlayer({ ...player, image: "", fileId: null });
                            const input = document.getElementById("photoUpload");
                            if (input) input.value = "";
                          }}
                          disabled={uploading || loading || deleting}
                        >
                          <i className="bi bi-x-circle"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="photo-placeholder">
                        {uploading || deleting ? (
                          <>
                            <div className="spinner-border text-primary mb-2" role="status">
                              <span className="visually-hidden">Uploading...</span>
                            </div>
                            <p className="mb-0 small">Uploading...</p>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-camera-fill"></i>
                            <p className="mb-0 small">Upload Photo</p>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    id="photoUpload"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading || loading}
                    className="d-none"
                  />
                  <label
                    htmlFor="photoUpload"
                    className={`btn ${uploading || deleting ? "btn-secondary" : "btn-outline-primary"}`}
                  >
                    {uploading || deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-upload me-2"></i>
                        {preview ? "Change Photo" : "Upload Photo"}
                      </>
                    )}
                  </label>
                  <small className="d-block text-muted mt-2">
                    Optional - Maximum 500KB (JPG, PNG, WEBP)
                  </small>
                  {uploadError && (
                    <small className="d-block text-danger mt-1">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      Please try again after some time.
                    </small>
                  )}
                </div>

                {/* Player Name */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-person-fill me-2 text-primary"></i>
                    Player Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="(e.g., John Doe)"
                    required
                    value={player.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    disabled={loading}
                  />
                </div>

                {/* Player Number */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-hash me-2 text-primary"></i>
                    Jersey Number
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="e.g., 10, 7"
                    value={player.playerNumber}
                    onChange={(e) =>
                      handleChange("playerNumber", e.target.value)
                    }
                    disabled={loading}
                  />
                </div>

                {/* Mobile Number */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-telephone-fill me-2 text-primary"></i>
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    className="form-control form-control-lg"
                    placeholder="e.g., +8801712345678"
                    value={player.mobileNo}
                    onChange={(e) => handleChange("mobileNo", e.target.value)}
                    disabled={loading}
                  />
                </div>

                {/* Role */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-award-fill me-2 text-primary"></i>
                    Playing Role <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-lg"
                    value={player.role}
                    required
                    onChange={(e) => handleChange("role", e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Select Role</option>
                    <optgroup label="Cricket">
                      <option value="Batsman">Batsman</option>
                      <option value="Bowler">Bowler</option>
                      <option value="All-rounder">All-rounder</option>
                      <option value="Keeper">Wicket Keeper</option>
                    </optgroup>
                    <optgroup label="Football">
                      <option value="Goalkeeper">Goalkeeper</option>
                      <option value="Defender">Defender</option>
                      <option value="Midfielder">Midfielder</option>
                      <option value="Forward">Forward</option>
                    </optgroup>
                    
                  </select>
                </div>

                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 shadow-lg"
                    disabled={loading || uploading}
                    style={{
                      border: "none",
                      padding: "15px",
                      fontSize: "1.1rem",
                    }}
                  >
                    {loading || uploading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        {uploading ? "Uploading..." : "Registering..."}
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle-fill me-2"></i>
                        Complete Registration
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="card-footer bg-light text-center py-4">
            <p className="mb-3 text-muted">
              <i className="bi bi-info-circle me-2"></i>
              Already have an account or want to watch the auction?
            </p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Link to="/sign-in" className="btn btn-outline-primary">
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </Link>
              <Link to="/sign-up" className="btn btn-outline-success">
                <i className="bi bi-person-plus me-2"></i>
                Sign Up
              </Link>
              <Link to={`/guest/${gameId}`} className="btn btn-outline-info">
                <i className="bi bi-eye me-2"></i>
                View as Guest
              </Link>
            </div>
            <div className="mt-4 pt-3 border-top">
              <small className="text-muted">
                © 2025 SportsBid Arena. All rights reserved.
              </small>
            </div>
          </div>
        </div>

        {/* Additional Info Card */}
        <div
          className="text-center py-4"
          style={{ maxWidth: "800px", margin: "0 auto" }}
        >
          <div className="card border-0 shadow-sm bg-white bg-opacity-75">
            <div className="card-body p-4">
              <h3 className="h5 fw-bold mb-3">
                <i className="bi bi-lightbulb-fill text-warning me-2"></i>
                Registration Tips
              </h3>
              <div className="row g-3 text-start">
                <div className="col-md-6">
                  <div className="d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <small>
                      Provide accurate information for smooth auction process
                    </small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <small>
                      Upload a clear photo for better team selection
                    </small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <small>Double-check your mobile number for updates</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex">
                    <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <small>Select your primary playing role accurately</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Popup
        isOpen={popup.isOpen}
        onClose={() => setPopup({ isOpen: false, message: "", type: "info" })}
        message={popup.message}
        type={popup.type}
      />

      <style jsx>{`
        .player-photo-upload {
          position: relative;
          width: 150px;
          height: 150px;
          margin: 0 auto;
        }

        .photo-placeholder {
          width: 150px;
          height: 150px;
          border: 3px dashed #dee2e6;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .photo-placeholder:hover {
          border-color: #667eea;
          background: #f0f2ff;
        }

        .photo-placeholder i {
          font-size: 3rem;
          color: #6c757d;
          margin-bottom: 10px;
        }

        .photo-preview {
          position: relative;
          width: 150px;
          height: 150px;
        }

        .photo-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border: 4px solid #667eea;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
        }

        .remove-photo {
          position: absolute;
          top: -10px;
          right: -10px;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.25);
        }

        .btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
          transition: all 0.3s ease;
        }

        .card {
          animation: slideUp 0.5s ease-out;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .display-4 {
            font-size: 2rem;
          }
        }

        /* Dark Theme Styles */
        [data-theme="dark"] .photo-placeholder {
          background: #2d2d44;
          border-color: #4a4a6a;
        }

        [data-theme="dark"] .photo-placeholder:hover {
          border-color: #667eea;
          background: #3d3d5c;
        }

        [data-theme="dark"] .photo-placeholder i {
          color: #a0a0c0;
        }

        [data-theme="dark"] .photo-placeholder p {
          color: #a0a0c0;
        }

        [data-theme="dark"] .form-control,
        [data-theme="dark"] .form-select {
          background: #2d2d44;
          border-color: #4a4a6a;
          color: #e0e0e0;
        }

        [data-theme="dark"] .form-control::placeholder {
          color: #808080;
        }

        [data-theme="dark"] .form-control:focus,
        [data-theme="dark"] .form-select:focus {
          background: #2d2d44;
          border-color: #667eea;
          color: #e0e0e0;
        }

        [data-theme="dark"] .form-label {
          color: #e0e0e0;
        }

        [data-theme="dark"] .card {
          background: #1e1e2f !important;
          border-color: #4a4a6a;
        }

        [data-theme="dark"] .card-body {
          background: #1e1e2f;
        }

        [data-theme="dark"] .card-footer {
          background: #2d2d44 !important;
          border-color: #4a4a6a;
        }

        [data-theme="dark"] .text-muted {
          color: #a0a0a0 !important;
        }

        [data-theme="dark"] h2,
        [data-theme="dark"] h3 {
          color: #e0e0e0;
        }
      `}</style>
    </div>
  );
};

export default PlayerReg;
